/**
 * Phase 9.7 Batch A — 🎬 Live Recording Controls
 * -----------------------------------------------
 * Renders a 🔴 REC chip + consent banner inside a Live Beam room, plus
 * a host-only Start / Stop toggle. Pure presentational; the parent owns
 * the POST calls.
 */
import { Button } from "@/components/ui/button";

const STATUS_LABELS = {
  starting: { dot: "bg-amber-400 animate-pulse", text: "Starting…" },
  active:   { dot: "bg-rose-500 animate-pulse", text: "REC" },
  stopping: { dot: "bg-amber-400 animate-pulse", text: "Saving…" },
  complete: { dot: "bg-emerald-400", text: "Saved" },
  failed:   { dot: "bg-zinc-500", text: "Failed" },
};

const LiveRecordingControls = ({ recording, isHost, busy, onStart, onStop }) => {
  const status = recording?.status;
  const active = status === "starting" || status === "active";

  // Nothing to render if not recording and viewer isn't host (no toggle UI).
  if (!active && !isHost) return null;

  return (
    <div
      data-testid="live-recording-controls"
      className="mb-3 p-3 rounded-xl bg-black/60 border border-rose-500/40 flex flex-wrap items-center gap-3"
    >
      {active && (
        <div
          className="flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/50"
          data-testid="live-rec-chip"
        >
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_LABELS[status].dot}`} />
          <span className="text-[11px] font-black tracking-wider uppercase text-rose-200">
            {STATUS_LABELS[status].text}
          </span>
        </div>
      )}

      {active && (
        <p className="text-xs text-rose-100/85 flex-1 min-w-[180px]" data-testid="live-rec-consent">
          This room is being recorded. Saved replays may be shared publicly by the host.
        </p>
      )}

      {isHost && (
        <div className="flex gap-2 ml-auto">
          {!active ? (
            <Button
              size="sm"
              onClick={onStart}
              disabled={busy}
              className="bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-xs"
              data-testid="live-rec-start-btn"
            >
              🎬 Start Recording
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onStop}
              disabled={busy}
              className="border-rose-400/60 text-rose-100 hover:bg-rose-500/15 text-xs"
              data-testid="live-rec-stop-btn"
            >
              ⏹ Stop Recording
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveRecordingControls;
